import { FC, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FlexBox, BrgButton, ButtonVariant, useToasts } from "@brg/ui";
import { useModal, modalControllerSelector } from "core";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { LayoutConst } from "core";
import { IconCloseMob } from "../SelectGeoLocationTagsModal/SelectGeoLocationTagsModal";
import { StyledDropdown } from "shared/pages/HomeworkCheck/components/Statements/styles";
import { API, CompetitionAPI, Lang, Endpoints } from "@brg/api";
import { useProfile } from "@brg/auth";
import { Student } from "shared/pages/HomeworkCheck/components/HomeworkEstimate/types";
import { getFormatDate } from "shared/helpers/LMS/convertToDate";

const ModalWrapper = styled(FlexBox)`
  width: 720px;
  max-width: 90vw;
  background: #FEFEFE;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  position: relative;
  padding: 32px 40px 40px 40px;
  flex-direction: column;
  gap: 24px;

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 16px;
    width: 100%;
    max-width: 100vw;
  }
`;

const CloseWrapper = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
  z-index: 1;

  @media (max-width: ${LayoutConst.screen.s}px) {
    top: 12px;
    right: 12px;
  }
`;

const Title = styled.div`
  font-family: 'Inter';
  font-weight: 600;
  font-size: 28px;
  line-height: 34px;
  letter-spacing: -0.02em;
  color: #151514;
  margin-bottom: 8px;
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-weight: 600;
    font-size: 17px;
    line-height: 22px;
    letter-spacing: normal;
  }
`;

const FormField = styled(FlexBox)`
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const Label = styled.label`
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: -0.02em;
  color: #151514;
`;

const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
`;


const ButtonContainer = styled(FlexBox)`
  flex-direction: row;
  gap: 16px;
  width: 100%;
  margin-top: 8px;
`;

const CancelButton = styled(BrgButton)`
  flex: 1;
  font-weight: 600;
`;

const SubmitButton = styled(BrgButton)`
  flex: 1;
  font-weight: 600;
`;

export const RetakeModal: FC = () => {
  const { t, i18n: { language } } = useTranslation();
  const modal = useModal();
  const modalState = useSelector(modalControllerSelector);
  const profile = useProfile();
  const toasts = useToasts();
  const [retakeName, setRetakeName] = useState<{ value: string; label: string } | null>(null);
  const [reason, setReason] = useState<{ value: string; label: string } | null>(null);
  const [retakeNameOptions, setRetakeNameOptions] = useState<{ value: string; label: string }[]>([]);
  const [isLoadingRetakes, setIsLoadingRetakes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const selectedStudentIds = modalState?.args?.selectedStudentIds || [];
  const tagId = modalState?.args?.tagId || null;
  const studentsData = modalState?.args?.studentsData || [];
  const moduleId = modalState?.args?.moduleId || null;
  const onConfirm = modalState?.args?.onConfirm;
  const onCancel = modalState?.args?.onCancel;
 
  const isSingleStudent = selectedStudentIds.length === 1;
  
  useEffect(() => {
    if (tagId) {
      setIsLoadingRetakes(true);
      //@ts-ignore
      CompetitionAPI.GetList({
        isRetake: true,
        Exams: true,
        byTagIds: [tagId]
      })
        .then((r) => r.json())
        .then((r) => {
          const competitions = API.toLang(r.data || [], language) as Array<{ lmsModuleProjectId: string; title: string; competitionId: string; endDate?: string }>;
          const options = competitions.map((competition: any) => {
            const title = Lang(competition.title || [], language) || competition.competitionId;
            let label = title;
            
            if (competition?.endDate) {
              try {
                const endDate = new Date(competition.endDate);
                if (!isNaN(endDate.getTime())) {
                  const formattedDate = getFormatDate(endDate);
                  label = `${title} (${formattedDate})`;
                }
              } catch (error) {
              }
            }
            
            return {
              value: competition?.lmsModuleProjectId,
              label
            };
          });
          setRetakeNameOptions(options);
        })
        .catch((error) => {
          console.error('Ошибка при загрузке списка пересдач:', error);
        })
        .finally(() => {
          setIsLoadingRetakes(false);
        });
    }
  }, [tagId, language]);
  
  const reasonOptions = useMemo(() => [
    { value: "valid", label: t('lms.scores_report.retake_reason_valid') },
    { value: "invalid", label: t('lms.scores_report.retake_reason_invalid') }
  ], [t]);

  const handleConfirm = async () => {
    if (!retakeName?.value || !moduleId || !profile?.accountId) {
      return;
    }

    if (isSingleStudent && !reason?.value) {
      return;
    }

    setIsSubmitting(true);

    try {
      const retakeReason = reason?.value === "valid" ? 1 : reason?.value === "invalid" ? 0 : 0;
      const destModuleProjectId = retakeName.value;

      const promises = selectedStudentIds.map(async (studentId: string) => {
        const student = studentsData.find((s: Student) => s.studentId === studentId);
        if (!student) {
          const errorMessage = `Студент с ID ${studentId} не найден`;
          toasts.error(errorMessage);
          throw new Error(errorMessage);
        }

        const payload = {
          studentId: student.studentId,
          studentProjectId: student.studentProjectId || "",
          sourceModuleProjectId: student.moduleProjectId || moduleId,
          destModuleProjectId: destModuleProjectId,
          retakeReason: retakeReason,
          reasonDescription: reason?.label,
          retakeByAccountId: profile.accountId,
        };

        const response = await API.request()
          .endpoint(Endpoints.geoGateway)
          .url('/api/LMS/PushToRetake')
          .post()
          .body(payload)
          .send();

        try {
          const text = await response.text();
          if (text.trim()) {
            return JSON.parse(text);
          }
        } catch (error) {
        }
        
        return null;
      });

      await Promise.all(promises);
      
      const successMessage = isSingleStudent 
        ? t('lms.scores_report.student_sent_to_retake')
        : t('lms.scores_report.students_sent_to_retake');
      toasts.info(successMessage);
      
      onConfirm?.(retakeName.value, isSingleStudent ? reason?.value : undefined);
      modal.hide();
    } catch (error: any) {
      if (error?.message) {
        toasts.error(error.message);
      } else {
        toasts.error('Произошла ошибка при отправке на пересдачу');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    modal.hide();
  };

  return (
    <ModalWrapper>
      <CloseWrapper>
        <IconCloseMob onClick={() => modal.hide()} />
      </CloseWrapper>
      
      <Title>{t('lms.scores_report.send_to_retake')}</Title>
      
      <FormField>
        <Label>{t('lms.scores_report.retake_name_label')}</Label>
        <DropdownWrapper>
          <StyledDropdown
            options={retakeNameOptions}
            value={retakeName}
            placeholder={t('lms.scores_report.select_retake_name')}
            custom={{
              customBackgroundColor: '#FEFEFE',
              customBackgroundColorHover: '#F9F9F9',
              customTextColor: '#151514',
              customBorderColor: '#D0D0D0',
              customBorderColorHover: '#D0D0D0',
              customPlaceholderColor: '#666666',
              customScrollColor: '#B6B6B5'
            }}
            onChange={(item: { value: string; label: string } | null) => {
              setRetakeName(item);
            }}
          />
        </DropdownWrapper>
      </FormField>

      {isSingleStudent && (
        <FormField>
          <Label>{t('lms.scores_report.retake_reason_label')}</Label>
          <DropdownWrapper>
            <StyledDropdown
              options={reasonOptions}
              value={reason}
              placeholder={t('lms.scores_report.select_reason')}
              custom={{
                customBackgroundColor: '#FEFEFE',
                customBackgroundColorHover: '#F9F9F9',
                customTextColor: '#151514',
                customBorderColor: '#D0D0D0',
                customBorderColorHover: '#D0D0D0',
                customPlaceholderColor: '#666666',
                customScrollColor: '#B6B6B5'
              }}
              onChange={(item: { value: string; label: string } | null) => {
                setReason(item);
              }}
            />
          </DropdownWrapper>
        </FormField>
      )}

      <ButtonContainer>
        <CancelButton variant={ButtonVariant.Hollow} onClick={handleCancel}>
          {t('lms.scores_report.cancel')}
        </CancelButton>
        <SubmitButton 
          variant={ButtonVariant.Filled} 
          onClick={handleConfirm}
          disabled={isSubmitting || !retakeName || (isSingleStudent && !reason)}
        >
          {isSubmitting ? 'Отправка...' : t('lms.scores_report.send')}
        </SubmitButton>
      </ButtonContainer>
    </ModalWrapper>
  );
};
import { FC, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FlexBox, BrgButton, ButtonVariant, useToasts } from "@brg/ui";
import { useModal, modalControllerSelector } from "core";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { LayoutConst } from "core";
import { IconCloseMob } from "../SelectGeoLocationTagsModal/SelectGeoLocationTagsModal";
import { StyledDropdown } from "shared/pages/HomeworkCheck/components/Statements/styles";
import { API, CompetitionAPI, Lang, Endpoints } from "@brg/api";
import { useProfile } from "@brg/auth";
import { Student } from "shared/pages/HomeworkCheck/components/HomeworkEstimate/types";
import { getFormatDate } from "shared/helpers/LMS/convertToDate";

const ModalWrapper = styled(FlexBox)`
  width: 720px;
  max-width: 90vw;
  background: #FEFEFE;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  position: relative;
  padding: 32px 40px 40px 40px;
  flex-direction: column;
  gap: 24px;

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 16px;
    width: 100%;
    max-width: 100vw;
  }
`;

const CloseWrapper = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
  z-index: 1;

  @media (max-width: ${LayoutConst.screen.s}px) {
    top: 12px;
    right: 12px;
  }
`;

const Title = styled.div`
  font-family: 'Inter';
  font-weight: 600;
  font-size: 28px;
  line-height: 34px;
  letter-spacing: -0.02em;
  color: #151514;
  margin-bottom: 8px;
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-weight: 600;
    font-size: 17px;
    line-height: 22px;
    letter-spacing: normal;
  }
`;

const FormField = styled(FlexBox)`
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const Label = styled.label`
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: -0.02em;
  color: #151514;
`;

const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
`;


const ButtonContainer = styled(FlexBox)`
  flex-direction: row;
  gap: 16px;
  width: 100%;
  margin-top: 8px;
`;

const CancelButton = styled(BrgButton)`
  flex: 1;
  font-weight: 600;
`;

const SubmitButton = styled(BrgButton)`
  flex: 1;
  font-weight: 600;
`;

export const RetakeModal: FC = () => {
  const { t, i18n: { language } } = useTranslation();
  const modal = useModal();
  const modalState = useSelector(modalControllerSelector);
  const profile = useProfile();
  const toasts = useToasts();
  const [retakeName, setRetakeName] = useState<{ value: string; label: string } | null>(null);
  const [reason, setReason] = useState<{ value: string; label: string } | null>(null);
  const [retakeNameOptions, setRetakeNameOptions] = useState<{ value: string; label: string }[]>([]);
  const [isLoadingRetakes, setIsLoadingRetakes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const selectedStudentIds = modalState?.args?.selectedStudentIds || [];
  const tagId = modalState?.args?.tagId || null;
  const studentsData = modalState?.args?.studentsData || [];
  const moduleId = modalState?.args?.moduleId || null;
  const onConfirm = modalState?.args?.onConfirm;
  const onCancel = modalState?.args?.onCancel;
 
  const isSingleStudent = selectedStudentIds.length === 1;
  
  useEffect(() => {
    if (tagId) {
      setIsLoadingRetakes(true);
      //@ts-ignore
      CompetitionAPI.GetList({
        isRetake: true,
        Exams: true,
        byTagIds: [tagId]
      })
        .then((r) => r.json())
        .then((r) => {
          const competitions = API.toLang(r.data || [], language) as Array<{ lmsModuleProjectId: string; title: string; competitionId: string; endDate?: string }>;
          const options = competitions.map((competition: any) => {
            const title = Lang(competition.title || [], language) || competition.competitionId;
            let label = title;
            
            if (competition?.endDate) {
              try {
                const endDate = new Date(competition.endDate);
                if (!isNaN(endDate.getTime())) {
                  const formattedDate = getFormatDate(endDate);
                  label = `${title} (${formattedDate})`;
                }
              } catch (error) {
              }
            }
            
            return {
              value: competition?.lmsModuleProjectId,
              label
            };
          });
          setRetakeNameOptions(options);
        })
        .catch((error) => {
          console.error('Ошибка при загрузке списка пересдач:', error);
        })
        .finally(() => {
          setIsLoadingRetakes(false);
        });
    }
  }, [tagId, language]);
  
  const reasonOptions = useMemo(() => [
    { value: "valid", label: t('lms.scores_report.retake_reason_valid') },
    { value: "invalid", label: t('lms.scores_report.retake_reason_invalid') }
  ], [t]);

  const handleConfirm = async () => {
    if (!retakeName?.value || !moduleId || !profile?.accountId) {
      return;
    }

    if (isSingleStudent && !reason?.value) {
      return;
    }

    setIsSubmitting(true);

    try {
      const retakeReason = reason?.value === "valid" ? 1 : reason?.value === "invalid" ? 0 : 0;
      const destModuleProjectId = retakeName.value;

      const promises = selectedStudentIds.map(async (studentId: string) => {
        const student = studentsData.find((s: Student) => s.studentId === studentId);
        if (!student) {
          const errorMessage = `Студент с ID ${studentId} не найден`;
          toasts.error(errorMessage);
          throw new Error(errorMessage);
        }

        const payload = {
          studentId: student.studentId,
          studentProjectId: student.studentProjectId || "",
          sourceModuleProjectId: student.moduleProjectId || moduleId,
          destModuleProjectId: destModuleProjectId,
          retakeReason: retakeReason,
          reasonDescription: reason?.label,
          retakeByAccountId: profile.accountId,
        };

        const response = await API.request()
          .endpoint(Endpoints.geoGateway)
          .url('/api/LMS/PushToRetake')
          .post()
          .body(payload)
          .send();

        try {
          const text = await response.text();
          if (text.trim()) {
            return JSON.parse(text);
          }
        } catch (error) {
        }
        
        return null;
      });

      await Promise.all(promises);
      
      const successMessage = isSingleStudent 
        ? t('lms.scores_report.student_sent_to_retake')
        : t('lms.scores_report.students_sent_to_retake');
      toasts.info(successMessage);
      
      onConfirm?.(retakeName.value, isSingleStudent ? reason?.value : undefined);
      modal.hide();
    } catch (error: any) {
      if (error?.message) {
        toasts.error(error.message);
      } else {
        toasts.error('Произошла ошибка при отправке на пересдачу');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    modal.hide();
  };

  return (
    <ModalWrapper>
      <CloseWrapper>
        <IconCloseMob onClick={() => modal.hide()} />
      </CloseWrapper>
      
      <Title>{t('lms.scores_report.send_to_retake')}</Title>
      
      <FormField>
        <Label>{t('lms.scores_report.retake_name_label')}</Label>
        <DropdownWrapper>
          <StyledDropdown
            options={retakeNameOptions}
            value={retakeName}
            placeholder={t('lms.scores_report.select_retake_name')}
            custom={{
              customBackgroundColor: '#FEFEFE',
              customBackgroundColorHover: '#F9F9F9',
              customTextColor: '#151514',
              customBorderColor: '#D0D0D0',
              customBorderColorHover: '#D0D0D0',
              customPlaceholderColor: '#666666',
              customScrollColor: '#B6B6B5'
            }}
            onChange={(item: { value: string; label: string } | null) => {
              setRetakeName(item);
            }}
          />
        </DropdownWrapper>
      </FormField>

      {isSingleStudent && (
        <FormField>
          <Label>{t('lms.scores_report.retake_reason_label')}</Label>
          <DropdownWrapper>
            <StyledDropdown
              options={reasonOptions}
              value={reason}
              placeholder={t('lms.scores_report.select_reason')}
              custom={{
                customBackgroundColor: '#FEFEFE',
                customBackgroundColorHover: '#F9F9F9',
                customTextColor: '#151514',
                customBorderColor: '#D0D0D0',
                customBorderColorHover: '#D0D0D0',
                customPlaceholderColor: '#666666',
                customScrollColor: '#B6B6B5'
              }}
              onChange={(item: { value: string; label: string } | null) => {
                setReason(item);
              }}
            />
          </DropdownWrapper>
        </FormField>
      )}

      <ButtonContainer>
        <CancelButton variant={ButtonVariant.Hollow} onClick={handleCancel}>
          {t('lms.scores_report.cancel')}
        </CancelButton>
        <SubmitButton 
          variant={ButtonVariant.Filled} 
          onClick={handleConfirm}
          disabled={isSubmitting || !retakeName || (isSingleStudent && !reason)}
        >
          {isSubmitting ? 'Отправка...' : t('lms.scores_report.send')}
        </SubmitButton>
      </ButtonContainer>
    </ModalWrapper>
  );
};
